/**
 * Workshop widget gallery — every specialized card widget rendered at its
 * exact production canvas size with realistic, complete mock data.
 *
 * Each card renders inside a fixed-size box matching the node size the
 * canvas assigns on creation (see frontend/src/lib/frame-defaults.ts,
 * mirrored in backend/apps/canvas/frame_defaults.py), with the component
 * stretched to fill it exactly like the h-full production root does
 * inside its node. Captions show width × height plus the sizing rule, so
 * what you see here is what lands on the workspace canvas — including
 * empty space when the content is shorter than the box, and inner scroll
 * when it is taller.
 *
 * Registered in workshop-routes.tsx at /workshop/shapes/widget-gallery.
 */

import { EventFrame } from "@/workshop/components/shapes/event-frame/event-frame";
import { AgendaFrame } from "@/workshop/components/shapes/agenda-frame/agenda-frame";
import { FlightFrame } from "@/workshop/components/shapes/flight-frame/flight-frame";
import {
  WeatherFrame,
  type WeatherDay,
} from "@/workshop/components/shapes/weather-frame/weather-frame";
import { ChartFrame } from "@/workshop/components/shapes/chart-frame/chart-frame";
import { PreviewCardFrame } from "@/workshop/components/shapes/preview-card-frame/preview-card-frame";
import { LinkPreviewFrame } from "@/workshop/components/shapes/link-preview-frame/link-preview-frame";
import { ChatFrame } from "@/workshop/components/shapes/chat-frame/chat-frame";
import { SlackFrame } from "@/workshop/components/shapes/slack-frame/slack-frame";
import { TodoFrame } from "@/workshop/components/shapes/todo-frame/todo-frame";
import {
  BoardFrame,
  type BoardColumn,
} from "@/workshop/components/shapes/board-frame/board-frame";
import { MusicFrame } from "@/workshop/components/shapes/music-frame/music-frame";
import { CalendarFrame } from "@/workshop/components/shapes/calendar-frame/calendar-frame";
import { FileFrame } from "@/workshop/components/shapes/file-frame/file-frame";

const WEATHER_DAYS: WeatherDay[] = [
  { day: "Today", icon: "Sun", high: 23, low: 14 },
  { day: "Tue", icon: "Sun", high: 22, low: 13 },
  { day: "Wed", icon: "Cloud", high: 19, low: 12 },
  { day: "Thu", icon: "Cloud", high: 18, low: 11 },
  { day: "Fri", icon: "Zap", high: 17, low: 10 },
];

const BOARD_COLUMNS: BoardColumn[] = [
  {
    id: "todo",
    label: "To do",
    dotColor: "bg-warning",
    cards: [
      { id: "c1", title: "Token audit for dark mode", meta: "P1 · J. Zhang" },
      { id: "c2", title: "Migrate legacy pills", meta: "v2.4 · A. Rivera" },
    ],
  },
  {
    id: "doing",
    label: "In progress",
    dotColor: "bg-status-selected",
    cards: [{ id: "c3", title: "Kanban recipe parity", meta: "M. Chen" }],
  },
  {
    id: "done",
    label: "Done",
    dotColor: "bg-accent",
    cards: [{ id: "c4", title: "Surface contract wiring", meta: "v2.3" }],
  },
];

const FULL_BOX = "h-[300px] w-[400px]";
const HALF_BOX = "h-[130px] w-[400px]";
const FILL = "h-full w-full";

function WidgetShell({
  name,
  size,
  rule,
  boxClass,
  testId,
  children,
}: {
  name: string;
  size: string;
  rule: string;
  boxClass: string;
  testId: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-2" data-testid={testId}>
      <div className={boxClass}>{children}</div>
      <div className="px-0.5">
        <p className="text-sm font-semibold text-white/85">{name}</p>
        <p
          className="mt-0.5 font-mono text-xs text-white/45"
          data-testid={`${testId}-size`}
        >
          {size} · {rule}
        </p>
      </div>
    </div>
  );
}

function Section({
  title,
  caption,
  testId,
  children,
}: {
  title: string;
  caption: string;
  testId: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-12" data-testid={testId}>
      <h2 className="text-sm font-semibold text-white/80">{title}</h2>
      <p className="mb-4 mt-1 text-sm text-white/40">{caption}</p>
      <div className="flex flex-wrap items-start gap-x-8 gap-y-10">
        {children}
      </div>
    </div>
  );
}

export function WidgetGalleryPage() {
  return (
    <div
      className="workshop-canvas-bg min-h-full p-8"
      data-testid="widget-gallery-page"
    >
      <style>{`.workshop-canvas-bg{background-color:#0f1115;background-image:linear-gradient(0deg,transparent,transparent),linear-gradient(135deg,#2b2f36 0%,#343a42 40%,#3d424a 70%,#3b2f2a 100%);position:relative}.workshop-canvas-bg::before{content:"";position:absolute;inset:0;z-index:0;pointer-events:none;opacity:0.1;background:radial-gradient(circle at 22% 18%,rgba(156,132,255,0.18),transparent 58%),radial-gradient(circle at 72% 70%,rgba(111,168,198,0.12),transparent 60%)}.workshop-canvas-bg>*{position:relative;z-index:1}`}</style>
      <div className="mb-8">
        <h1 className="text-lg font-bold text-white">Widget Gallery</h1>
        <p className="mt-1 max-w-2xl text-sm text-white/40">
          Every specialized card widget rendered at its exact production
          canvas size, with realistic complete data. Captions show width ×
          height plus the sizing rule: eleven widgets always land at 400 ×
          300, four compact ones at half tiles. Overflowing content scrolls
          inside the card.
        </p>
      </div>

      <Section
        title="Fixed cards — 400 × 300"
        caption="Eleven widgets always land at exactly this size. Overflowing content scrolls inside the card instead of resizing it."
        testId="widget-gallery-section-fixed"
      >
        <WidgetShell
          name="Flight"
          size="400 × 300"
          rule="fixed"
          boxClass={FULL_BOX}
          testId="widget-gallery-shell-flight"
        >
          <FlightFrame data-testid="widget-gallery-flight" className={FILL} />
        </WidgetShell>
        <WidgetShell
          name="Agenda · 3 items"
          size="400 × 300"
          rule="fixed · rows scroll"
          boxClass={FULL_BOX}
          testId="widget-gallery-shell-agenda"
        >
          <AgendaFrame data-testid="widget-gallery-agenda" className={FILL} />
        </WidgetShell>
        <WidgetShell
          name="Weather · 5 days"
          size="400 × 300"
          rule="fixed · rows scroll"
          boxClass={FULL_BOX}
          testId="widget-gallery-shell-weather"
        >
          <WeatherFrame
            data-testid="widget-gallery-weather"
            className={FILL}
            place="Berlin"
            temp={21}
            condition="Mostly Sunny"
            high={23}
            low={14}
            days={WEATHER_DAYS}
          />
        </WidgetShell>
        <WidgetShell
          name="Board · 3 columns"
          size="400 × 300"
          rule="fixed · scrolls both ways"
          boxClass={FULL_BOX}
          testId="widget-gallery-shell-board"
        >
          <BoardFrame
            data-testid="widget-gallery-board"
            className={FILL}
            title="Design System — rollout"
            path="Design / Neutral Base"
            columns={BOARD_COLUMNS}
          />
        </WidgetShell>
        <WidgetShell
          name="Chart"
          size="400 × 300"
          rule="fixed"
          boxClass={FULL_BOX}
          testId="widget-gallery-shell-chart"
        >
          <ChartFrame
            data-testid="widget-gallery-chart"
            className={FILL}
            title="BTC / USD — 12d"
            insight="lowest in 30d"
            unit="$"
          />
        </WidgetShell>
        <WidgetShell
          name="Preview card"
          size="400 × 300"
          rule="fixed"
          boxClass={FULL_BOX}
          testId="widget-gallery-shell-preview-card"
        >
          <PreviewCardFrame
            data-testid="widget-gallery-preview-card"
            className={FILL}
            imageUrl="https://picsum.photos/seed/aman-tokyo/640/400"
            title="Aman Tokyo — Retreat in Otemachi"
            source="Preview · aman-tokyo.lovable.app"
          />
        </WidgetShell>
        <WidgetShell
          name="Chat"
          size="400 × 300"
          rule="fixed"
          boxClass={FULL_BOX}
          testId="widget-gallery-shell-chat"
        >
          <ChatFrame data-testid="widget-gallery-chat" className={FILL} />
        </WidgetShell>
        <WidgetShell
          name="Slack"
          size="400 × 300"
          rule="fixed"
          boxClass={FULL_BOX}
          testId="widget-gallery-shell-slack"
        >
          <SlackFrame data-testid="widget-gallery-slack" className={FILL} />
        </WidgetShell>
        <WidgetShell
          name="Todo"
          size="400 × 300"
          rule="fixed"
          boxClass={FULL_BOX}
          testId="widget-gallery-shell-todo"
        >
          <TodoFrame data-testid="widget-gallery-todo" className={FILL} />
        </WidgetShell>
        <WidgetShell
          name="Music"
          size="400 × 300"
          rule="fixed"
          boxClass={FULL_BOX}
          testId="widget-gallery-shell-music"
        >
          <MusicFrame data-testid="widget-gallery-music" className={FILL} />
        </WidgetShell>
        <WidgetShell
          name="Calendar"
          size="400 × 300"
          rule="fixed"
          boxClass={FULL_BOX}
          testId="widget-gallery-shell-calendar"
        >
          <CalendarFrame
            data-testid="widget-gallery-calendar"
            className={FILL}
          />
        </WidgetShell>
      </Section>

      <Section
        title="Half tiles — 400 × 130"
        caption="Two halves plus one 40px grid gap fill exactly one 400 × 300 cell (130 + 40 + 130), so compact widgets tile cleanly with full cards."
        testId="widget-gallery-section-half"
      >
        <WidgetShell
          name="Event"
          size="400 × 130"
          rule="half tile"
          boxClass={HALF_BOX}
          testId="widget-gallery-shell-event"
        >
          <EventFrame data-testid="widget-gallery-event" className={FILL} />
        </WidgetShell>
        <WidgetShell
          name="File"
          size="400 × 130"
          rule="half tile"
          boxClass={HALF_BOX}
          testId="widget-gallery-shell-file"
        >
          <FileFrame data-testid="widget-gallery-file" className={FILL} />
        </WidgetShell>
        <WidgetShell
          name="Weather · today only"
          size="400 × 130"
          rule="half tile · no days"
          boxClass={HALF_BOX}
          testId="widget-gallery-shell-weather-half"
        >
          <WeatherFrame
            data-testid="widget-gallery-weather-half"
            className={FILL}
            place="Berlin"
            temp={21}
            condition="Mostly Sunny"
            high={23}
            low={14}
            days={[]}
          />
        </WidgetShell>
        <WidgetShell
          name="Link preview · one link"
          size="400 × 130"
          rule="half tile · 2+ links scroll"
          boxClass={HALF_BOX}
          testId="widget-gallery-shell-link-preview"
        >
          <LinkPreviewFrame
            data-testid="widget-gallery-link-preview"
            className={FILL}
          />
        </WidgetShell>
      </Section>
    </div>
  );
}
